// TutorialManager.js
import React, { useState, useEffect, createContext } from 'react';

// Create context for tutorial functions
export const TutorialContext = createContext();

const TutorialModal = ({ isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

    // Check for mobile view on component mount and resize
    useEffect(() => {
      const checkMobileView = () => {
        setIsMobile(window.innerWidth <= 767);
      };
  
      // Check initial view
      checkMobileView();
  
      // Add resize listener
      window.addEventListener('resize', checkMobileView);
  
      // Cleanup listener
      return () => {
        window.removeEventListener('resize', checkMobileView);
      };
    }, []);
  
    // Safely get current step image
    const getCurrentImage = () => {
      const currentStepData = tutorialSteps[currentStep];
      return isMobile 
        ? (currentStepData.mobileImage || currentStepData.desktopImage)
        : currentStepData.desktopImage;
    };

  const tutorialSteps = [
    {
      title: "What is TalkConnect?",
      content: (
        <div className="space-y-3">
          <p className="text-gray-600 leading-relaxed">
            Everyone has a story to share or a moment they wish someone understood. 
          </p>
          <p className="text-gray-600 leading-relaxed">
            Whether you need to talk or listen, TalkConnect pairs you with someone who's 
            ready to be there. Choose your role, set your mood, and start a heartfelt 
            conversation—no distractions, no judgment, just real connection.
          </p>
        </div>
      ),
      desktopImage: "/images/welcome.svg",
      mobileImage: "/images/welcome-mobile.svg"
    },
    {
      title: "Who is the Talker?",
      content: (
        <div className="space-y-3">
          <p className="text-gray-600 leading-relaxed">
            A Talker is someone who is ready to share their thoughts, feelings, or experiences.
          </p>
          <p className="text-gray-600 leading-relaxed">
            Looking for a fresh perspective or someone to hear you out? Switch to Talker 
            and get connected in an instant.
          </p>
          <p className="text-gray-600 leading-relaxed">
            Don't worry - your Listener can engage and respond too!
          </p>
        </div>
      ),
      desktopImage: "/images/talker.svg",
      mobileImage: "/images/talker-mobile.svg"
    },
    {
      title: "Who is the Listener?",
      content: (
        <div className="space-y-3">
          <p className="text-gray-600 leading-relaxed">
            As a Listener, you provide support and engage in conversation.
          </p>
          <p className="text-gray-600 leading-relaxed">
            While the Talker leads, you can share your perspective and help guide 
            meaningful discussions.
          </p>
          <p className="text-gray-600 leading-relaxed">
            Ready to be there for someone? Switch to Listener and make a difference.
          </p>
        </div>
      ),
      desktopImage: "/images/listener.svg",
      mobileImage: "/images/listener-mobile.svg"
    }
  ];

  const handleNext = () => {
    if (currentStep < tutorialSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // Tutorial completed
      onClose();
      setCurrentStep(0); // Reset for next time
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl w-full max-w-xl mx-4 overflow-hidden 
                      mobile:max-w-[90%] mobile:w-[90%]"> {/* Mobile width adjustment */}
        {/* Progress Bar */}
        <div className="w-full h-1 bg-gray-100">
          <div 
            className="h-full bg-teal-500 transition-all duration-300"
            style={{ width: `${((currentStep + 1) / tutorialSteps.length) * 100}%` }}
          />
        </div>

        {/* Content */}
        <div className="p-6 mobile:p-4"> {/* Reduced padding for mobile */}
          {/* Step Content */}
          <div className="min-h-[280px] mobile:min-h-[240px]">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 mobile:text-lg">
              {tutorialSteps[currentStep].title}
            </h2>
            <p className="text-gray-600 mobile:text-sm">
              {tutorialSteps[currentStep].content}
            </p>

            {/* Image */}
            {tutorialSteps[currentStep].desktopImage || tutorialSteps[currentStep].mobileImage ? (
              <img
                src={getCurrentImage()}
                alt={tutorialSteps[currentStep].title}
                className="mt-6 h-80 w-full object-contain bg-gray-50 rounded-lg
                           mobile:h-48 mobile:object-cover"
              />
            ) : (
              <div className="mt-6 h-80 bg-gray-50 rounded-lg flex items-center justify-center
                              mobile:h-48">
                <span className="text-gray-400">Image will go here</span>
              </div>
            )}
          </div>

          {/* Navigation */}
          <div className="flex justify-between items-center mt-6 mobile:mt-4">
            <div className="flex space-x-2">
              {tutorialSteps.map((_, index) => (
                <div
                  key={index}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    index === currentStep ? 'bg-teal-500' : 'bg-gray-200'
                  }`}
                />
              ))}
            </div>
            <button
              onClick={handleNext}
              className="px-6 py-2 bg-teal-500 text-white rounded-lg font-medium 
                         hover:bg-teal-600 transition-colors mobile:px-4 mobile:py-1.5 mobile:text-sm"
            >
              {currentStep === tutorialSteps.length - 1 ? 'Get Started' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const TutorialProvider = ({ children }) => {
  const [showTutorial, setShowTutorial] = useState(false);

  useEffect(() => {
    // Check if it's the user's first visit
    const hasSeenTutorial = localStorage.getItem('hasSeenTutorial');
    if (!hasSeenTutorial) {
      // First time visitor - show tutorial
      setShowTutorial(true);
    }
  }, []);

  const handleTutorialComplete = () => {
    // Mark tutorial as seen
    localStorage.setItem('hasSeenTutorial', 'true');
    setShowTutorial(false);
  };

  const showTutorialAgain = () => {
    setShowTutorial(true);
  };

  return (
    <TutorialContext.Provider value={{ showTutorialAgain }}>
      <TutorialModal 
        isOpen={showTutorial} 
        onClose={handleTutorialComplete}
      />
      {children}
    </TutorialContext.Provider>
  );
};

// Export the modal and context provider
export { TutorialModal };